import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { Router } from "@angular/router";
import { ImageService } from "../../../core/services/image.service";

import {
  Gallery,
  GalleryItem,
  ImageItem,
  ThumbnailsPosition,
  GalleryRef
} from "@ngx-gallery/core";

import {
  faStar,
  faShareAltSquare,
  faExternalLinkAlt,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import { Lightbox } from "@ngx-gallery/lightbox";
import { WindowRef } from "../../../window-ref.service";
import { environment } from "../../../../environments/environment";
import { I18nService } from "../../services/i18n.service";
import { PropertyTypeHelperService } from "../../services/property-helper.service";
import { CommonGenericService } from "../../services/common.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ListingsService } from "../../../core";
import { FeaturedListingService } from "../../../core/services/featured-listing.service";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { SiilaTranslationService } from "../../../core/services/siila-translation.service";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";

export interface FeaturedListingSpaceRange {
  minArea?: number;
  maxArea?: number;
  minPositions?: number;
  maxPositions?: number;
  minAskingPriceOrRent?: number;
  maxAskingPriceOrRent?: number;
}

export interface FeaturedListingNamedRef {
  id?: number;
  name?: string;
  slug?: string;
}

export interface FeaturedListingCardData {
  id: number;
  title?: string;
  buildingTitle?: string;
  address?: string;
  buildingAddress?: string;
  type?: string;
  premium?: boolean;
  featured?: boolean;
  rentableArea?: number;
  mainImageId?: number;
  companyLogoImageId?: number;
  preferredCurrency?: string;
  buildingTypeId?: number;
  buildingType?: FeaturedListingNamedRef;
  classType?: FeaturedListingNamedRef;
  subMarket?: FeaturedListingNamedRef;
  region?: FeaturedListingNamedRef;
  city?: FeaturedListingNamedRef & {
    countryState?: FeaturedListingNamedRef;
  };
  propertySubTypes?: FeaturedListingNamedRef[];
  spaceRangesDTO?: FeaturedListingSpaceRange;
  listingPhotos?: unknown[];
  buildingPhoto?: { image?: { id?: number } };
  offeredByCompany?: FeaturedListingNamedRef;
  offeredByUser?: {
    company?: FeaturedListingNamedRef;
    firstNameAndLastName?: string;
  };
  /** Relative SPOT detail path, in the requested language (sent by the backend). */
  detailsURL?: string;
}

@Component({
  selector: "featured-listing-card",
  templateUrl: "./featured-listing-card.component.html",
  styleUrls: ["./featured-listing-card.component.scss"]
})
export class FeaturedListingCard implements OnInit {
  @Input() listing: FeaturedListingCardData;
  @Input() listing2: FeaturedListingCardData;
  @Input() latamCountry: string;
  @Input() detail = false;
  @Input() hideLogo = false;
  @Output() cardClick = new EventEmitter<FeaturedListingCardData>();
  /**
   * The card's destination, known up front so it renders as a real href
   * (crawlable, middle-click/cmd-click, announced as a link). Null only while
   * the backend does not yet send `detailsURL` — the card then falls back to
   * resolving the URL on click, as before.
   */
  cardHref: string | null = null;
  listingPhotos: GalleryItem[];
  faStar: IconDefinition;
  listingGallery: GalleryRef;
  propertyType: string;
  listingSubtypes: string;
  faShareAltSquare: IconDefinition;
  faExternalLinkAlt: IconDefinition;
  spotListingDetailsURL: string;
  coworkingSubTypeId: number = 2003;
  spotType: string;
  isMobile = false;
  buildingTitle: string;
  companyName: string;
  platformId = environment.platformId;

  constructor(
    public router: Router,
    private imageService: ImageService,
    private _lightbox: Lightbox,
    private gallery: Gallery,
    private windowRef: WindowRef,
    private i18nService: I18nService,
    private propertyHelperService: PropertyTypeHelperService,
    public commonService: CommonGenericService,
    private snackbar: MatSnackBar,
    private featureListingService: FeaturedListingService,
    private translateService: TranslateService,
    private siilaTranslationService: SiilaTranslationService,
    public breakpointObserver: BreakpointObserver
  ) {
    this.breakpointObserver
      .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
      .subscribe(result => {
        this.isMobile = result.matches;
      });
  }

  ngOnInit(): void {
    this.buildingTitle = this.siilaTranslationService.getTranslation(
      this.detail ? this.listing.title : this.listing?.buildingTitle
    );
    this.companyName = this.listing?.offeredByCompany?.name || "";
    this.cardHref = this.resolveCardHref();
    this.imageService.latamCountry = this.latamCountry;
    this.faStar = faStar;
    this.listingGallery = this.gallery.ref("listingPhotosGallery");
    this.listingGallery.setConfig({
      thumbPosition: ThumbnailsPosition.Bottom
      //itemTemplate: this.itemTemplate,
      //gestures: false,
      //imageSize: 'cont',
      // loadingIcon: 'Loading...'
    });
    this.faShareAltSquare = faShareAltSquare;
    this.faExternalLinkAlt = faExternalLinkAlt;
    this.propertyType = this.propertyHelperService.getPropertyTypeTranslationName(
      !this.detail ? this.listing.buildingTypeId : this.listing.buildingType.id
    );
    if (!this.propertyType && this.listing.buildingType) {
      this.propertyType = this.i18nService.getTranslation(
        this.listing.buildingType.name
      );
    }
    this.spotType = this.propertyHelperService.getSpotBuildingTypeTranslation(
      this.listing.type
    );
    if (this.listing.propertySubTypes && this.listing.propertySubTypes.length) {
      //const propertySubTypes: any = this.commonService.translateLocaleText(
      //  this.listing.propertySubTypes,
      //   "name"
      //);

      const propertySubTypes: any = this.listing.propertySubTypes.map(item => {
        const isValidJSON: boolean = this.isJSON(item["name"]);
        item["name"] = isValidJSON
          ? this.siilaTranslationService.getTranslation(item["name"])
          : item["name"];
        return item;
      });

      this.listingSubtypes = this.commonService.transformArrayStringList(
        propertySubTypes,
        "name"
      );
    }
    /* this.settingNewDetailsURL(); */
    /*  this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.settingNewDetailsURL();
    }); */
  }

  private settingNewDetailsURL() {
    this.featureListingService
      .checkListingDetailRoute(
        this.listing.id,
        this.i18nService.getCurrentLanguage()
      )
      .then(newURLFormat => {
        this.spotListingDetailsURL = `${newURLFormat.detailsURL}`;

        this.spotListingDetailsURL = this.i18nService.addCurrentLanguageToPath(
          this.spotListingDetailsURL
        );
      });
  }

  getMediumImg(id: number) {
    return this.imageService.mediumThumbnail(id);
  }

  getMediumLargeImg(id: number) {
    return this.imageService.mediumLargeThumbnail(id);
  }

  getLargeImg(id: number) {
    return this.imageService.largeThumbnail(id);
  }

  /**
   * Same destinations `openCard` opens: the in-app property page on detail
   * cards, otherwise the SPOT listing (absolute — SPOT is a separate site).
   */
  private resolveCardHref(): string | null {
    if (this.detail) {
      return this.getPropertyUrl(this.listing.id);
    }
    if (this.listing?.detailsURL) {
      return this.featureListingService.buildSpotDetailUrl(
        this.listing.detailsURL,
        this.latamCountry
      );
    }
    return null;
  }

  private getPropertyUrl(id: number): string {
    return this.i18nService.buildLocalizedPath(
      `${environment.applicationURL}/property/${id}`
    );
  }

  onCardClick($event: Event): void {
    if (this.cardHref) {
      // The anchor navigates natively; only keep the analytics event.
      this.cardClick.emit(this.listing);
      $event.stopImmediatePropagation();
      return;
    }
    this.openCard(this.listing.id, $event);
  }

  public async openCard(id: number, $event: Event) {
    this.cardClick.emit(this.listing);
    $event.stopImmediatePropagation();

    let lang = this.i18nService.getCurrentLanguage();

    if (window.location.href.includes("pt-br")) {
      lang = "pt-br";
    } else if (window.location.href.includes("en")) {
      lang = "en";
    }
    this.featureListingService.latamCountry = this.latamCountry;

    let res: any = await this.featureListingService
      .getSpotBuildingDetailURLFormat(id, lang)
      .toPromise();

    if (res && !this.detail) {
      let detailsURL = `${res}`;
      window.open(detailsURL, "_blank");
    } else {
      window.open(this.getPropertyUrl(id), "blank");
    }
  }

  public isCompanySameAsAUser(listing: FeaturedListingCardData) {
    return (
      listing &&
      listing.offeredByUser.company.name ===
        listing.offeredByUser.firstNameAndLastName
    );
  }

  private isJSON(str: string) {
    try {
      JSON.parse(str);
    } catch (error) {
      return false;
    }
    return true;
  }

  getSubHeaderCardInfo() {
    var address;
    if (this.detail && this.listing.address) {
      address = `${this.i18nService.getTranslation(
        this.listing.address
      )} ,${this.i18nService.getTranslation(this.listing.city?.name)} ,
      ${this.abbreviatedText(
        this.i18nService.getTranslation(this.listing?.city?.countryState?.name)
      )}`;
      return address;
    } else {
      address = `${this.i18nService.getTranslation(
        this.listing.buildingAddress
          ? this.listing.buildingAddress
          : this.listing.address
      )} `;
      return this.truncateText(address, 90);
    }
  }

  private truncateText(text: string, length: number) {
    if (!text) return;
    return text.length > length ? text.substring(0, length) + "..." : text;
  }

  getAvailableTexRegion() {
    if (this.detail) {
      return this.listing.subMarket
        ? this.listing?.subMarket?.name
        : this.i18nService.getTranslation(this.listing?.region?.name);
    } else {
      return this.getAvgAskingRent();
    }
  }

  getAvailableAreaText() {
    return this.commonService.getAvailableAreaText(
      this.listing.spaceRangesDTO,
      this.listing.type
    );
  }

  getAvgAskingRent() {
    return this.commonService.getAvgAskingRent(
      this.listing.spaceRangesDTO,
      this.listing,
      this.latamCountry
    );
  }

  public getCompanyLogoImg(id: number) {
    return id ? this.getMediumImg(id) : "../../../assets/no-company-logo.png";
  }

  abbreviatedText(text: string): string {
    const value = text.split(" ");
    if (value.length > 1) {
      const firstLetters = value.map(item => item.charAt(0)).join(".");
      return `${firstLetters}.`;
    } else {
      return text;
    }
  }
}
